import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Brain, FileText, TrendingUp, Sparkles, Upload, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export default function AIAnalysis({ candidate, candidateId, activities = [] }) {
  const [uploadingFile, setUploadingFile] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summary, setSummary] = useState("");
  const [predictiveScore, setPredictiveScore] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const queryClient = useQueryClient();

  const updateCandidateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.AcquisitionCandidate.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["acquisition-candidate"] });
    },
  });

  // Analyze uploaded document and extract financial data
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      // Upload file
      const { file_url } = await base44.integrations.Core.UploadFile({ file });

      // Extract structured data from document
      const result = await base44.integrations.Core.ExtractDataFromUploadedFile({
        file_url,
        json_schema: {
          type: "object",
          properties: {
            revenue: { type: "number", description: "Omsetning i NOK" },
            ebitda: { type: "number", description: "EBITDA i NOK" },
            employees: { type: "number", description: "Antall ansatte" },
            industry: { type: "string", description: "Bransje/sektor" },
            key_strengths: { 
              type: "array", 
              items: { type: "string" },
              description: "Viktigste styrker"
            },
            key_risks: { 
              type: "array", 
              items: { type: "string" },
              description: "Viktigste risikoer"
            },
          }
        }
      });

      if (result.status === "success" && result.output) {
        setExtractedData(result.output);
        
        // Auto-populate candidate data
        const multiple = result.output.revenue && result.output.ebitda 
          ? (candidate.discussed_price || 0) / result.output.ebitda 
          : null;

        await updateCandidateMutation.mutateAsync({
          id: candidateId,
          data: {
            revenue: result.output.revenue || candidate.revenue,
            ebitda: result.output.ebitda || candidate.ebitda,
            employees: result.output.employees || candidate.employees,
            industry: result.output.industry || candidate.industry,
            multiple: multiple || candidate.multiple,
          }
        });
      }
    } catch (error) {
      console.error("File analysis error:", error);
    } finally {
      setUploadingFile(false);
    }
  };

  // Generate predictive success score using AI
  const generatePredictiveScore = async () => {
    setAnalyzing(true);
    try {
      const prompt = `Analyser denne oppkjøpskandidaten og gi en prediktiv suksess-score (0-100):

Bedrift: ${candidate.company_name}
Bransje: ${candidate.industry || "Ikke spesifisert"}
Omsetning: ${candidate.revenue ? candidate.revenue.toLocaleString() + " NOK" : "Ikke oppgitt"}
EBITDA: ${candidate.ebitda ? candidate.ebitda.toLocaleString() + " NOK" : "Ikke oppgitt"}
Ansatte: ${candidate.employees || "Ikke oppgitt"}
Diskutert pris: ${candidate.discussed_price ? candidate.discussed_price.toLocaleString() + " NOK" : "Ikke oppgitt"}
Status: ${candidate.status}
Antall aktiviteter: ${activities.length}

${extractedData?.key_strengths ? `Styrker: ${extractedData.key_strengths.join(", ")}` : ""}
${extractedData?.key_risks ? `Risikoer: ${extractedData.key_risks.join(", ")}` : ""}

Vurder: Finansiell helse, markedsposisjon, vekstpotensial, risikoer, og match med våre kriterier.
Gi en score og kort begrunnelse.`;

      const result = await base44.integrations.Core.InvokeLLM({
        prompt,
        response_json_schema: {
          type: "object",
          properties: {
            score: { type: "number", description: "Prediktiv suksess-score 0-100" },
            reasoning: { type: "string", description: "Kort begrunnelse for scoren" },
            key_factors: { 
              type: "array", 
              items: { type: "string" },
              description: "3-5 nøkkelfaktorer som påvirker scoren"
            },
            recommendation: { 
              type: "string", 
              enum: ["strong_buy", "buy", "hold", "pass"],
              description: "Anbefaling"
            }
          }
        }
      });

      setPredictiveScore(result);
    } catch (error) {
      console.error("Predictive score error:", error);
    } finally {
      setAnalyzing(false);
    }
  };

  // Generate AI summary of candidate profile
  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const activitySummary = activities.slice(0, 5).map(a => 
        `- ${a.type}: ${a.title} (${new Date(a.date).toLocaleDateString("nb-NO")})`
      ).join("\n");

      const prompt = `Generer et profesjonelt og konsist sammendrag av denne oppkjøpskandidaten:

**${candidate.company_name}**

Bransje: ${candidate.industry || "Ikke spesifisert"}
Status: ${candidate.status}
Kontakt: ${candidate.contact_person}${candidate.customer_name ? ` (via kunde: ${candidate.customer_name})` : ""}

Finansielle data:
- Omsetning: ${candidate.revenue ? candidate.revenue.toLocaleString() + " NOK" : "Ikke oppgitt"}
- EBITDA: ${candidate.ebitda ? candidate.ebitda.toLocaleString() + " NOK" : "Ikke oppgitt"}
- Diskutert pris: ${candidate.discussed_price ? candidate.discussed_price.toLocaleString() + " NOK" : "Ikke oppgitt"}
- Multipel: ${candidate.multiple ? candidate.multiple + "x" : "Ikke beregnet"}
- Ansatte: ${candidate.employees || "Ikke oppgitt"}

Siste aktiviteter:
${activitySummary || "Ingen aktiviteter registrert"}

${candidate.notes ? `Notater: ${candidate.notes}` : ""}

Lag et strukturert sammendrag (2-3 avsnitt) som dekker:
1. Selskapsprofil og posisjon
2. Finansiell analyse og vurdering
3. Viktigste funn og neste steg`;

      const result = await base44.integrations.Core.InvokeLLM({
        prompt,
        add_context_from_internet: true
      });

      setSummary(result);
    } catch (error) {
      console.error("Summary generation error:", error);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const scoreColor = (score) => {
    if (score >= 75) return "text-emerald-600";
    if (score >= 50) return "text-amber-600";
    return "text-rose-600";
  };

  const recommendationBadge = (rec) => {
    const configs = {
      strong_buy: { label: "Sterk kjøp", color: "bg-emerald-100 text-emerald-700" },
      buy: { label: "Kjøp", color: "bg-blue-100 text-blue-700" },
      hold: { label: "Hold", color: "bg-amber-100 text-amber-700" },
      pass: { label: "Avslå", color: "bg-rose-100 text-rose-700" },
    };
    const config = configs[rec] || configs.hold;
    return <Badge className={cn("border-0", config.color)}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Document Analysis */}
      <Card className="p-6 bg-gradient-to-br from-lime-100 to-lime-100 border-0">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-zinc-900" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Dokumentanalyse</h3>
              <p className="text-sm text-slate-600">Last opp årsrapport eller finansdokument</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block">
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileUpload}
              disabled={uploadingFile}
              className="hidden"
              id="document-upload"
            />
            <Button
              type="button"
              variant="outline"
              disabled={uploadingFile}
              onClick={() => document.getElementById("document-upload").click()}
              className="w-full"
            >
              {uploadingFile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyserer dokument...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Last opp dokument
                </>
              )}
            </Button>
          </label>

          {extractedData && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-lime-200">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <h4 className="font-semibold text-slate-900">Ekstrahert data</h4>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {extractedData.revenue && (
                  <div>
                    <span className="text-slate-500">Omsetning:</span>
                    <p className="font-semibold">{extractedData.revenue.toLocaleString()} kr</p>
                  </div>
                )}
                {extractedData.ebitda && (
                  <div>
                    <span className="text-slate-500">EBITDA:</span>
                    <p className="font-semibold">{extractedData.ebitda.toLocaleString()} kr</p>
                  </div>
                )}
                {extractedData.employees && (
                  <div>
                    <span className="text-slate-500">Ansatte:</span>
                    <p className="font-semibold">{extractedData.employees}</p>
                  </div>
                )}
                {extractedData.industry && (
                  <div>
                    <span className="text-slate-500">Bransje:</span>
                    <p className="font-semibold">{extractedData.industry}</p>
                  </div>
                )}
              </div>
              {extractedData.key_strengths && extractedData.key_strengths.length > 0 && (
                <div className="mt-3">
                  <span className="text-xs text-slate-500">Styrker:</span>
                  <ul className="mt-1 space-y-1">
                    {extractedData.key_strengths.map((s, i) => (
                      <li key={i} className="text-xs text-slate-700">• {s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Predictive Score */}
      <Card className="p-6 bg-gradient-to-br from-lime-100 to-pink-50 border-0">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-zinc-900" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Prediktiv Analyse</h3>
              <p className="text-sm text-slate-600">AI-generert suksess-score</p>
            </div>
          </div>
          <Button
            onClick={generatePredictiveScore}
            disabled={analyzing}
            size="sm"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyserer...
              </>
            ) : (
              <>
                <Brain className="w-4 h-4 mr-2" />
                Generer Score
              </>
            )}
          </Button>
        </div>

        {predictiveScore && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-lime-200">
              <div>
                <p className="text-sm text-slate-500 mb-1">Prediktiv Score</p>
                <p className={cn("text-4xl font-bold", scoreColor(predictiveScore.score))}>
                  {predictiveScore.score}/100
                </p>
              </div>
              {predictiveScore.recommendation && recommendationBadge(predictiveScore.recommendation)}
            </div>

            <div className="p-4 bg-white rounded-lg border border-lime-200">
              <h4 className="font-semibold text-slate-900 mb-2">Begrunnelse</h4>
              <p className="text-sm text-slate-700 mb-3">{predictiveScore.reasoning}</p>
              
              {predictiveScore.key_factors && predictiveScore.key_factors.length > 0 && (
                <>
                  <h5 className="font-semibold text-slate-900 text-sm mb-2">Nøkkelfaktorer</h5>
                  <ul className="space-y-1">
                    {predictiveScore.key_factors.map((factor, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="text-zinc-900">•</span>
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* AI Summary */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-0">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">AI-Sammendrag</h3>
              <p className="text-sm text-slate-600">Automatisk generert profil</p>
            </div>
          </div>
          <Button
            onClick={generateSummary}
            disabled={generatingSummary}
            size="sm"
          >
            {generatingSummary ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Genererer...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generer Sammendrag
              </>
            )}
          </Button>
        </div>

        {summary && (
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
              {summary}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}